import { useMutation } from '@tanstack/react-query';
import { ApiError, post } from '@/shared/api/api-client';

/**
 * `POST /api/Master/ValidatePhoneNumber`
 *
 * Carrier validation for a phone number, via our own backend (Scrips.Master).
 *
 * ## This used to call Twilio from the browser, and must never do so again
 *
 * The previous version of this file called the Twilio Lookups API directly
 * with an `Authorization: Basic` header it built itself from two `VITE_*`
 * variables, and it carried hard-coded literal fallbacks for both. Vite
 * inlines `VITE_*` into the client bundle at build time, so the Twilio
 * credentials shipped in the JavaScript to every user, whether or not the
 * variables were set (SEC-1; same defect as PROD-2092 / PROD-2094, which
 * fixed the PM app this module was ported from).
 *
 * Anything the browser can send, a user can read. The credential belongs on
 * the server, which is the only place it can be kept.
 *
 * DO NOT reintroduce a client-side call here, and do not add a `VITE_*`
 * variable for a secret. If this endpoint is missing something, extend the
 * endpoint. `src/shared/lib/no-client-twilio-credential.guard.test.ts` fails if
 * a Twilio host, a `VITE_TWILIO_*` read or a credential-shaped literal appears
 * anywhere in `src/`.
 *
 * ## The number goes in the BODY
 *
 * A patient's phone number is PHI, and PHI in a URL lands in ingress logs,
 * access logs and browser history (PROD-2060). Hence POST with a body.
 *
 * ## No carrier verdict -> format-only verdict
 *
 * When we cannot get a carrier verdict, the hook falls back to a local E.164
 * format check and marks the result `formatOnly`. That covers:
 *
 *   - the endpoint is not deployed in this environment (404 / 405);
 *   - the service is down or erroring (5xx, network failure);
 *   - the server answers `lookupUnavailable` (Twilio down or not configured).
 *
 * A third-party outage, or a backend that has not caught up, must not block a
 * patient record, and it must never push us back to calling Twilio from the
 * browser. Client and auth errors (400, 401, 403, other 4xx) still reject:
 * those are bugs or session problems that a format check must not hide.
 */

interface ValidatePhoneNumberResponse {
  valid: boolean;
  /** Canonical E.164 formatting, when the lookup succeeded. */
  formatted?: string | null;
  /** ISO-3166 country code the lookup inferred ("AE", "US"). */
  countryCode?: string | null;
  /**
   * True when the server could not perform the lookup (Twilio unreachable,
   * erroring, or not configured). Separate from `valid` on purpose.
   */
  lookupUnavailable?: boolean;
}

interface ValidatePhoneParams {
  /** Full phone with country code, e.g. "+971501234567". */
  contactNumber: string;
}

export interface ValidatePhoneResult {
  valid: boolean;
  /** Canonical formatting if the carrier lookup succeeded. */
  formatted?: string;
  countryCode?: string;
  /**
   * True when no carrier verdict was available and `valid` comes from the
   * local E.164 format check alone.
   */
  formatOnly?: boolean;
}

export const VALIDATE_PHONE_ENDPOINT = '/api/Master/ValidatePhoneNumber';

/**
 * E.164 shape: "+", a non-zero first digit, 8 to 15 digits in total.
 * Spaces, dashes, dots and brackets are tolerated and ignored.
 */
export function isPlausibleE164(input: string): boolean {
  const compact = input.replace(/[\s\-().]/g, '');
  return /^\+[1-9]\d{7,14}$/.test(compact);
}

/** True when the failure means "no verdict available", not "bad request". */
function isVerdictUnavailable(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 404 || err.status === 405 || err.status >= 500;
  }
  // `fetch` rejects with a TypeError on a network failure (offline, DNS, CORS).
  return err instanceof TypeError;
}

function formatOnlyResult(contactNumber: string): ValidatePhoneResult {
  return { valid: isPlausibleE164(contactNumber), formatOnly: true };
}

export function useValidatePhoneNumber() {
  return useMutation<ValidatePhoneResult, Error, ValidatePhoneParams>({
    // Phone validation surfaces a field-inline error in its consumers. Opt
    // out of the global toast so users don't get both messages.
    meta: { skipGlobalError: true },
    mutationFn: async ({ contactNumber }) => {
      let data: ValidatePhoneNumberResponse | undefined;
      try {
        data = await post<ValidatePhoneNumberResponse>(VALIDATE_PHONE_ENDPOINT, {
          phoneNumber: contactNumber,
        });
      } catch (err) {
        if (isVerdictUnavailable(err)) return formatOnlyResult(contactNumber);
        throw err;
      }

      if (!data || data.lookupUnavailable) {
        return formatOnlyResult(contactNumber);
      }

      if (!data.valid) {
        return { valid: false };
      }

      return {
        valid: true,
        formatted: data.formatted ?? undefined,
        countryCode: data.countryCode ?? undefined,
      };
    },
  });
}
